import type React from 'react';
import { memo } from 'react';
import {
  Flex,
  Box,
  InputGroup,
  Input,
  InputRightElement,
  Tooltip,
  Center,
  Icon,
} from '@chakra-ui/react';
import { AlertTriangle } from '@kula-ai/icons/dual-tone/AlertTriangle';
import { PiQuestionDuotone } from 'react-icons/pi';
import SvgDragAndDrop from '@/assets/icons/table/DragAndDrop';
import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { type ListItemProps } from './aiAttributeList.types';
import { Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';

type AttributeRowProps = Omit<ListItemProps, 'index' | 'isDragging'> & {
  // Render-prop args from the dnd library; it ships no public types for them.
  provided: any;
  snapshot: any;
};

/**
 * The visual row, shared between the in-list draggable and the drag clone.
 * The clone is rendered via the Droppable's `renderClone` into document.body,
 * which keeps it visible above the drawer — a fixed-position preview left
 * inside the drawer gets mispositioned/clipped by the drawer panel.
 */
export const AttributeRow = ({
  item,
  onDelete,
  onInputChange,
  onAttributeBlur,
  isLessThanRequired,
  provided,
  snapshot,
}: AttributeRowProps) => {
  const error =
    item.is_valid === false
      ? {
          type: item.valid_type,
          reason: item.reason,
        }
      : undefined;
  return (
    <Flex
      ref={provided.innerRef}
      {...provided.draggableProps}
      bg="base-white"
      boxShadow={snapshot.isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : 'none'}
      borderRadius="6px"
      border={snapshot.isDragging ? '2px solid' : '1px solid transparent'}
      borderColor={snapshot.isDragging ? 'primary-200' : 'transparent'}
      transition="all 0.2s ease"
      p="8px"
      alignItems="center"
      gap="6px"
      justifyContent="space-between"
      role="group"
      flexDir="row"
      overflow="hidden"
      w="100%"
      data-attribute-container={item.id}
      minH="fit-content"
      style={{
        ...provided.draggableProps.style,
      }}
    >
      <Flex alignItems="center" gap="10px" flex="1 1 0" minW={0}>
        <Box
          {...provided.dragHandleProps}
          cursor={snapshot.isDragging ? 'grabbing' : 'grab'}
          opacity="1"
          color={snapshot.isDragging ? 'primary-500' : 'secondary-400'}
          transition="color 0.2s ease"
          _hover={{ color: 'primary-500', bg: 'primary-50' }}
          p="4px"
          borderRadius="4px"
          bg={snapshot.isDragging ? 'primary-50' : 'transparent'}
        >
          <Icon as={SvgDragAndDrop} width="16px" height="16px" />
        </Box>
        <InputGroup
          borderRadius="6px"
          color="secondary-700"
          border="1px solid"
          borderColor={
            error?.type === 'error'
              ? 'error-200'
              : error?.type === 'warning'
                ? 'warning-200'
                : 'secondary-100'
          }
        >
          <Input
            value={item.description}
            size="sm"
            onChange={(e) => onInputChange(item.id, e.target.value)}
            onBlur={() => onAttributeBlur(item.id)}
            maxW="100%"
            minW="0"
            border="none"
            borderRadius="6px"
            p="12px"
            pr={error ? '36px' : '12px'}
            _hover={{ border: 'none', bg: 'secondary-25' }}
            _focus={{ border: 'none' }}
            _active={{ border: 'none' }}
            _focusVisible={{ border: 'none' }}
            color="secondary-700"
            placeholder="Add an attribute"
            data-attribute-id={item.id}
            h="44px"
          />
          {error && (
            <InputRightElement h="100%">
              <Flex
                alignItems="center"
                justifyContent="center"
                h="100%"
                w="100%"
              >
                <Tooltip label={error.reason} hasArrow placement="top">
                  <Flex alignItems="center" justifyContent="center">
                    {error.type === 'error' ? (
                      <Icon
                        as={AlertTriangle}
                        color="error-200"
                        boxSize="16px"
                      />
                    ) : error.type === 'warning' ? (
                      <Icon
                        as={PiQuestionDuotone as any}
                        color="warning-200"
                        boxSize="16px"
                      />
                    ) : undefined}
                  </Flex>
                </Tooltip>
              </Flex>
            </InputRightElement>
          )}
        </InputGroup>
      </Flex>
      <Tooltip
        label="At least three attributes are required to score candidates accurately"
        isDisabled={!isLessThanRequired}
        hasArrow
        placement="top"
      >
        <Center
          p="8px"
          cursor="pointer"
          border="1px solid"
          bg="base-white"
          borderRadius="6px"
          borderColor="secondary-100"
          onClick={() => !isLessThanRequired && onDelete(item.id)}
          opacity={isLessThanRequired ? 0.5 : 1}
          // _hover={
          //   !isLessThanRequired ? { borderColor: 'error-200' } : {}
          // }
          transition="all 0.2s ease"
        >
          <Icon as={Trash01} />
        </Center>
      </Tooltip>
    </Flex>
  );
};

export const AiAttributeListItem: React.FC<ListItemProps> = memo(
  ({
    item,
    index,
    onDelete,
    onInputChange,
    onAttributeBlur,
    isDragging: _isDragging,
    isLessThanRequired,
  }) => (
    <Draggable
      draggableId={`id-${item.id}`}
      index={index}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <AttributeRow
          item={item}
          onDelete={onDelete}
          onInputChange={onInputChange}
          onAttributeBlur={onAttributeBlur}
          isLessThanRequired={isLessThanRequired}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  )
);

AiAttributeListItem.displayName = 'AiAttributeListItem';
export default AiAttributeListItem;
